import { Dropdown, Button, Checkbox, Input, Form } from "semantic-ui-react";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { getDropdownSemanticOptionsFromData, useFetchData, removeProp } from "../../../helpers/GlobalHelper";
import LmsMessage from "./LmsMessage";
import { useBCGlobalReducer } from "../../BCGlobalStore";

const defaultCreatePayload = {
  "columns": "[]",
  "filters": "{}",
  "groups": "[]",
  "sort": "[]",
  "search": "",
  "isInactive": false,
  "description": null,
  "controller": "SPA"
};

const LmsTableFilterSubmitModal = (
  {
    tableFilterReducerInstance,
    gridUniqueIdString,
    refreshSettingsOptions,
    setSettingOptions,
    isLoaderVisible
  }
) => {
  const [{ fetchData }] = useFetchData();
  const [successMessage, setSuccessMessage] = useState(null as any);
  const [submitting, setSubmitting] = useState(false);
  const [{
    filterSubmitModal,
    allViewOptions,
    currentViewValue,
    currentViewIsDefault,
    currentViewIsSystem,
    currentViewIsWidthSaved,
    formattedFilterWithStrings,
    columns
  }, tableReducerDispatch]: any = tableFilterReducerInstance;
  const globalDispatch = useBCGlobalReducer()[1];
  const [formValues, setFormValues] = useState(null as any);
  const [activeMode, setActiveMode] = useState("");
  const [selectedModifyOption, setSelectedModifyOption] = useState("");
  const { isVisible } = filterSubmitModal;
  const filterSubmitRef = useRef(null as any);
  const labelText = activeMode === "modify" ? "Select an existing configuration to modify." : "Name";

  useEffect(() => {
    setFormValues({
      ...formValues || {},
      isDefault: currentViewIsDefault,
      isSystem: currentViewIsSystem,
      isWidthSaved: currentViewIsWidthSaved
    });
  }, [currentViewIsDefault, currentViewIsSystem, currentViewIsWidthSaved]);

  useEffect(() => {
    if (currentViewValue) {
      setSelectedModifyOption(currentViewValue);
    }
  }, [currentViewValue]);

  const modifySettingsOptions = useMemo(() => {
    if (allViewOptions) {
      return getDropdownSemanticOptionsFromData(allViewOptions);
    }

    return [];
  }, [activeMode, allViewOptions]);

  const handleClose = () => {
    tableReducerDispatch({
      type: GlobalConstants.LmsTableReducerCases.SET_FILTER_SUBMIT_MODAL,
      payload: { isVisible: false, posX: "0", posY: "0" }
    });

    setFormValues(null);
    setActiveMode("");
    setSubmitting(false);
  };

  useEffect(() => {
    const handleOutsideSubmitClick = (e) => {
      if (filterSubmitRef.current && filterSubmitRef.current.contains(e.target)) {
        return;
      }
      else {
        handleClose();
      }
    };

    if (isVisible) {
      filterSubmitRef?.current?.setAttribute("listener", "true");
      document.addEventListener("mousedown", handleOutsideSubmitClick);
    }

    return () => {
      if (filterSubmitRef?.current?.getAttribute("listener") !== "true") {
        document.removeEventListener("mousedown", handleOutsideSubmitClick);
      }
    };
  }, [isVisible]);

  const handleModifyOptionChange = (e, { value }) => {
    const foundOptions = allViewOptions.find((option) => option.id === value);
    if (foundOptions) {
      setFormValues({
        ...formValues,
        isDefault: foundOptions.isDefault,
        isSystem: foundOptions.isSystem,
        isWidthSaved: foundOptions.isWidthSaved
      });
    }

    setSelectedModifyOption(value);
  };
  const handleModifyCheckboxChange = (_, { checked }: any, name) => {
    setFormValues({ ...formValues, [name]: checked });
  };

  const handleSave = async () => {
    setSubmitting(true);
    const { isDefault, isSystem, isWidthSaved } = formValues || {};
    let payload: any;
    const foundOptions = allViewOptions?.find((option) => option.id === selectedModifyOption);
    const foundOptionsColumns = foundOptions && !isWidthSaved ? JSON.parse(foundOptions?.columns) : columns;
    const updatedColumns = [] as any[];
    const formattedOptionsColumns = JSON.parse(formattedFilterWithStrings?.columns);
    formattedOptionsColumns.forEach((optionColumn) => {
      const foundOptionColumn = foundOptionsColumns.find((foundColumn) => foundColumn.prop === optionColumn.prop);
      if (foundOptionColumn?.width) {
        updatedColumns.push({ ...optionColumn, width: foundOptionColumn.width });
      }
      else {
        updatedColumns.push({ ...optionColumn, width: "" });
      }
    });

    const updatedFilters = { ...formattedFilterWithStrings, columns: JSON.stringify(updatedColumns) };
    if (activeMode === "modify") {
      const foundOptions = allViewOptions.find((option) => option.id === selectedModifyOption);
      payload = {
        ...foundOptions || {},
        ...updatedFilters,
        isDefault: !!isDefault,
        isSystem: !!isSystem,
        isWidthSaved: !!isWidthSaved,
      };
    }
    else {
      payload = {
        ...defaultCreatePayload,
        ...updatedFilters,
        internalName: formValues.configName,
        name: formValues.configName,
        isDefault: !!isDefault,
        isSystem: !!isSystem,
        isWidthSaved: !!isWidthSaved,
        action: gridUniqueIdString,
        pageId: allViewOptions?.[0]?.pageId || "",
      };
    }

    const { data, error } = await fetchData("gridviews", {
      fetchMethod: activeMode === "modify" ? "PUT" : "POST",
      data: removeProp(payload, ["page", "userAccount"])
    });

    setSubmitting(false);
    if (error) {
      globalDispatch({
        type: GlobalConstants.BCStoreReducerCases.SET_INFO,
        payload: {
          header: GlobalConstants.DefaultValues.messageHeaderText,
          message: error?.message,
          type: GlobalConstants.ErrorLevels.ERROR
        },
      });
    }
    else {
      if (activeMode !== "modify" && data) {
        tableReducerDispatch({
          type: GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_VALUE,
          payload: data?.id || data
        });
      }
      refreshSettingsOptions();
      setSuccessMessage(true);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    const { error } = await fetchData(`gridviews/${selectedModifyOption}`, { fetchMethod: "DELETE" });
    setSubmitting(false);
    if (error) {
      setSuccessMessage(error);
    }
    else {
      setSettingOptions(allViewOptions?.filter((option) => option.id !== selectedModifyOption) || []);
      tableReducerDispatch({
        type: GlobalConstants.LmsTableReducerCases.DELETE_CURRENT_VIEW_OPTION,
        payload: selectedModifyOption
      });
      setSuccessMessage(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (confirm("Are you sure you want to delete this settings?")) {
      await handleDelete();
    }
  };

  return <>
    {isVisible && !isLoaderVisible && <div
      ref={filterSubmitRef}
      className="lms-table-column-filter-container"
      style={{
        opacity: 1,
        position: "absolute",
        top: "100%"
      }}
    >
      <Form loading={submitting}>
        {successMessage && <LmsMessage
          text="The operation was completed successfully."
          header="Success!"
          isInfo={true}
          autoCloseIn={2000}
          funcOnClose={() => setSuccessMessage(null)}
        />}
        <div className="lms-table-submit-filter-container">
          {!activeMode && <>
            <div className="lms-table-submit-filter-label">Available Settings</div>
            <div className="lms-table-submit-filter-buttons-container">
              {allViewOptions?.length > 0 && <Button
                onClick={() => setActiveMode("modify")}
                fluid
                compact
              >
                Modify these
              </Button>}
              <Button
                onClick={() => setActiveMode("create")}
                fluid
                compact
              >
                Create New
              </Button>
            </div>
          </>}
          {activeMode && <>
            <div className="lms-table-submit-filter-inputs-container">
              <div className="modify-settings-options-label">{labelText}</div>
              {activeMode === "modify" && <>
                <Dropdown
                  options={modifySettingsOptions}
                  selection
                  value={selectedModifyOption}
                  onChange={handleModifyOptionChange as any}
                  className="lms-table-action-dropdown-button"
                  fluid
                />
              </>}
              {activeMode === "create" && <>
                <Input
                  onChange={(e, { value }) => setFormValues({ ...formValues, configName: value })}
                  value={formValues?.configName || ""}
                  fluid
                />
              </>}
            </div>
            <div className="lms-table-submit-filter-modify-checkboxes-container">
              <Checkbox
                label="Set as default"
                onChange={(e, data) => handleModifyCheckboxChange(e, data, "isDefault")}
                checked={!!formValues?.isDefault}
              />
              <Checkbox
                label="System View"
                onChange={(e, data) => handleModifyCheckboxChange(e, data, "isSystem")}
                checked={!!formValues?.isSystem}
              />
              <Checkbox
                label="Save column widths"
                onChange={(e, data) => handleModifyCheckboxChange(e, data, "isWidthSaved")}
                checked={!!formValues?.isWidthSaved}
              />
            </div>
            <div className="lms-table-submit-filter-buttons-container">
              <Button
                onClick={handleSave}
                compact
              >
                Save
              </Button>
              {activeMode === "modify" && <>
                <Button
                  onClick={handleConfirmDelete}
                  compact
                >
                  Delete
                </Button>
              </>}
            </div>
          </>}
          <div className="lms-table-submit-filter-close-button-container">
            <Button
              onClick={handleClose}
              compact
            >
              Close
            </Button>
          </div>
        </div>
      </Form>
    </div>}
  </>;
};

export default LmsTableFilterSubmitModal;